<template>
  <Drawer
    v-model:visible="drawerVisible"
    position="right"
    :modal="true"
    :dismissable="true"
    :style="drawerStyle"
    :pt="{
      header: '',
      content: 'flex flex-col h-full min-h-0 overflow-hidden p-0',
    }"
    class="h-full relative"
    data-test-id="tax-injector-drawer"
  >
    <template #header>
      <div class="flex items-center justify-between w-full pr-4">
        <span>Tax Injector Records</span>
        <Button
          :icon="
            isFullscreen ? 'pi pi-window-minimize' : 'pi pi-window-maximize'
          "
          variant="text"
          size="small"
          @click="toggleFullscreen"
          :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
          data-test-id="fullscreen-toggle-btn"
        />
      </div>
    </template>

    <div class="h-full flex flex-col min-h-0">
      <!-- Back button -->
      <div class="px-4 pt-2 bg-surface-0 dark:bg-surface-900">
        <Button
          label="Back to Upload History"
          icon="pi pi-arrow-left"
          @click="handleClose"
          variant="text"
          size="small"
          data-test-id="back-to-history-btn"
        />
      </div>

      <!-- Loading State -->
      <div
        v-if="isLoading"
        class="flex-1 flex items-center justify-center p-4"
        data-test-id="records-loading"
      >
        <div class="flex flex-col items-center gap-3">
          <i class="pi pi-spin pi-spinner text-4xl text-surface-400 dark:text-surface-500"></i>
          <p class="text-surface-600 dark:text-surface-300">Loading records...</p>
        </div>
      </div>

      <!-- Records Content -->
      <div v-else class="flex-1 overflow-y-auto p-4">
        <TaxInjectorRecordsContent
          ref="recordsContentRef"
          :records="records"
          :loading="isLoading"
          :external-id="externalId || ''"
          :injector="store.selectedInjector"
          @refresh="handleRefresh"
          @download-file="handleDownloadFile"
        />
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import Drawer from 'primevue/drawer'
import Button from 'primevue/button'
import TaxInjectorRecordsContent from '@/components/taxinjector/TaxInjectorRecordsContent.vue'
import { useTaxInjectorStore } from '@/stores/taxInjectorStore'
import { errorToastWithDetails } from '@/util/toasts'
import type { TaxInjectorRecord, TaxInjectorSummary } from '@/types/taxInjector'
import { normalizeError } from '@/util/errorNormalizer'

interface Props {
  visible: boolean
  externalId: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  'download-file': [injector: TaxInjectorSummary]
}>()

const toast = useToast()
const { authState } = useFrontegg()
const store = useTaxInjectorStore()

// Local state
const isFullscreen = ref(false)
const records = ref<TaxInjectorRecord[]>([])
const isLoading = ref(false)
const recordsContentRef = ref()

// Computed for v-model binding
const drawerVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => {
    emit('update:visible', value)
  },
})


const drawerStyle = computed(() => {
  if (isFullscreen.value) {
    return { width: '100vw', maxWidth: '100vw' }
  }
  return { width: '80vw', maxWidth: '1200px' }
})

// Functions
function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value
}

function handleClose() {
  store.clearSelection()
  records.value = []
  drawerVisible.value = false
}

async function loadRecords() {
  if (!props.externalId) {
    return
  }

  isLoading.value = true
  records.value = []

  try {
    const response = await store.fetchRecords(authState, props.externalId)
    records.value = response.Content || []
  } catch (error) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    records.value = []
  } finally {
    isLoading.value = false
  }
}

async function handleRefresh() {
  await loadRecords()
}

function handleDownloadFile(injector: TaxInjectorSummary) {
  emit('download-file', injector)
  
  // Clear loading state in child component after a brief delay
  setTimeout(() => {
    if (recordsContentRef.value) {
      recordsContentRef.value.clearDownloadingState()
    }
  }, 1000)
}

// Watch for externalId changes to load records
watch(
  () => props.externalId,
  async (newExternalId) => {
    if (newExternalId && props.visible) {
      await loadRecords()
    } else {
      records.value = []
    }
  },
  { immediate: true },
)

// Also watch for visibility changes
watch(
  () => props.visible,
  async (newVisible) => {
    if (newVisible && props.externalId) {
      await loadRecords()
    } else if (!newVisible) {
      records.value = []
    }
  },
)
</script>

